import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {addCourseChats, fetchModes} from "../services/MentorApiService";
import { Course, Mode } from "../types/courseTypes";

interface ChatDialogProps {
    open: boolean;
    onClose: () => void;
    course: Course;
    onConfirm: () => void;
}

const ChatDialog = ({ open, onClose, course, onConfirm }: ChatDialogProps) => {
    const navigate = useNavigate();
    const [modes, setModes] = useState<Mode[]>([]);
    const [selectedMode, setSelectedMode] = useState<Mode | null>(null);
    const [chatName, setChatName] = useState(`${course.courseName} Suggestions Chat`);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

    const modeDescriptions: { [key: string]: { title: string; description: string } } = {
        Comprehensive: {
            title: "Holistic",
            description: "Offers comprehensive suggestions for embedding AI literacy across all six SAIL framework categories into your course design."
        },
        Selective: {
            title: "Digest",
            description: "Provides targeted suggestions for embedding AI literacy by focusing on up to three selected SAIL framework categories in your course design."
        }
    };

    const handleFetchModes = () => {
        fetchModes()
            .then((response) => {
                setModes(response.data.modes.filter((mode: { id: number; }) => mode.id !== 3));
                setErrorMessage("");
            })
            .catch((error) => {
                setSnackbarOpen(true);
                setErrorMessage(error);
            });
    };

    const handleCreateChat = () => {
        if (selectedMode) {
            const params = { mode_id: selectedMode.id, name: chatName};
            addCourseChats(course.id, params) // Note: This might be intended as a different API call, e.g., createChat
                .then((response) => {
                    navigate(`/courses/${course?.id}/chat/${response.data.chatId}`);
                    setErrorMessage("");
                })
                .catch((error) => {
                    setSnackbarOpen(true);
                    setErrorMessage(error);
                });
        }
    };

    useEffect(() => {
        handleFetchModes();
    }, []);

    const handleModeSelect = (mode: Mode) => {
        setSelectedMode(mode);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Start A New Suggestions Chat</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label="Chat Name"
                    value={chatName}
                    onChange={(e) => setChatName(e.target.value)}
                    sx={{ mb: 3, mt: 1 }}
                    helperText="Feel free to edit the chat name provided."
                />
                <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                    Please choose a focus mode for your chat.
                </Typography>
                <Grid container spacing={2} sx={{ maxWidth: 600 }}>
                    {modes.map((mode) => (
                        <Grid size={6} key={mode.id}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    backgroundColor: selectedMode?.name === mode.name ? 'primary.main' : 'background.paper',
                                    color: selectedMode?.name === mode.name ? 'primary.contrastText' : 'text.primary',
                                    transition: 'background-color 0.3s, color 0.3s',
                                    '&:hover': {
                                        backgroundColor: selectedMode?.name === mode.name ? 'primary.dark' : 'action.hover',
                                    },
                                }}
                                onClick={() => handleModeSelect(mode)}
                            >
                                <CardContent>
                                    <Typography variant="h6">{modeDescriptions[mode.name].title}</Typography>
                                    <Typography variant="body2">{modeDescriptions[mode.name].description}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!selectedMode || !chatName}
                    onClick={handleCreateChat}
                >
                    Proceed
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChatDialog;